import type { IFilesystem } from '@livx.cc/wcli/core';
import type { ChatLike, ChatResponse, Message, StreamChunk, ToolCall, MessageContent, ContentPart } from './llm';
import { contentText, imagePart, elideStaleImages, elideImages, messageHasImage, expandImagesForSend, sendBytes, sendBytesPer } from './llm';
import { type AgentTool, type ToolContext, type HostBridge, type UserQuestion, defaultTools, makeContext, toWireTools, truncateOutput } from './tools';
import { SandboxJobRegistry, makeJobTools } from './tools.jobs';
import { askUserQuestionTool } from './host';
import { loadSkills, scanSkills } from './skills';
import { loadCommands, scanCommands } from './commands';
import { loadMemory } from './memory';
import { loadInstructions } from './instructions';
import { makeTaskTool, makeTaskBatchTool } from './subagent';
import { loadAgents } from './agents';
import { checkpointTools } from './OverlayFilesystem';
import type { Hooks } from './hooks';
import { PermissionPolicy, planMode, composeHooks } from './permissions';
import { forComponent } from './logging';
import { checkSyntax } from './lint';
import { reasoningToChatFragment, type ReasoningEffort } from './reasoning';

const log = forComponent('Agent');

/** Read a non-negative integer env override, guarded for non-node (edge/browser) builds. */
function envInt(key: string, fallback: number): number {
  const n = typeof process !== 'undefined' ? Number(process.env?.[key]) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/** A browser-bridge MCP tool name (`mcp__<server>__<tool>` where the server name mentions "browser").
 *  These return huge DOM/screenshot payloads — their USE is the signal that triggers adaptive trimming. */
function isBrowserTool(name: string): boolean {
  return name.startsWith('mcp__') && /browser/i.test(name.slice(5).split('__')[0]);
}

/** A thrown provider error that is really a cancel (barge-in / Ctrl-C), not a crash. The abort flag can
 *  lag the throw under a race, so we message-match across providers: fetch AbortError, Node ABORT_ERR,
 *  groq/SDK "operation was aborted", ConnectError [canceled]. Mirrors the CLI's cursor-abort guard. */
function isAbortError(err: unknown): boolean {
  const e = err as any;
  const blob = `${e?.message ?? ''} ${e?.name ?? ''} ${e?.code ?? ''} ${e?.cause?.name ?? ''}`;
  return /operation was aborted|\bAbortError\b|ABORT_ERR|\[canceled\]/i.test(blob);
}

export interface RunResult {
  text: string;
  steps: number;
  /** Why the loop ended. The middle group are automatic kill-switches (budget/abuse guards).
   *  `needs_input` is NOT a failure: the agent hit a decision that was the user's to make with no
   *  human reachable, so it stopped and raised the question (see `question`) instead of guessing.
   *  Answer it and resume the session to continue. */
  finishReason: 'stop' | 'max_steps' | 'budget' | 'timeout' | 'loop' | 'max_tool_calls' | 'aborted' | 'error' | 'needs_input' | 'empty';
  messages: Message[];
  /** The parked question — present iff `finishReason === 'needs_input'`. */
  question?: UserQuestion;
  /** Accumulated token usage across all turns (non-stream path). With prompt caching,
   *  promptTokens includes cached reads/writes; the cache splits ride along for exact pricing. */
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number; cacheCreationTokens?: number; cacheReadTokens?: number };
  /** True if ANY turn's usage was estimated (provider gave none) rather than exact — lets the UI mark cost `~`. */
  usageEstimated?: boolean;
  error?: unknown;
}

/** What a manual `/compact` actually freed (token estimates, ~4 chars/token). */
export interface CompactResult { tokensBefore: number; tokensAfter: number; target: number; cap: number; removed: number }

export class AgentOptions {
  /** Any ai.libx.js AIClient (or a FakeAIClient). */
  ai!: ChatLike;
  /** Filesystem backend — MemFilesystem, NodeDiskFilesystem, IndexedDbFilesystem, …
   *  OPTIONAL: if omitted, the agent lazily defaults to a JAILED real disk rooted at `process.cwd()`
   *  (secrets hidden by DEFAULT_DENY) — resolved via a dynamic node import at first run, so the edge/
   *  browser build (which always passes its own fs) never pulls in `node:fs`. Pass `fs` explicitly to
   *  use any other backend (and to keep full control of the jail). */
  fs?: IFilesystem;
  model = 'anthropic/claude-sonnet-4-6';
  systemPrompt =
    'You are a coding agent operating on a virtual filesystem. Use the `bash` tool to explore (ls, grep, find, cat) and the `Read`/`Edit` tools to view and modify files. Always Read a file before Editing it — and before overwriting it with `Write` or a whole-file `ApplyEdits`: Write replaces the ENTIRE file, so blindly writing over an existing one destroys its unseen contents. If a file already exists, Read it first, then merge your changes (or Edit the specific part). When multiple tool calls are independent (e.g. several Reads or Greps with no dependency between them), issue them together in a single turn rather than one at a time. When the task is complete, reply with a short summary and make no further tool calls.\n' +
    'Recovery: every turn\'s file changes are checkpointed. If the user asks to undo/revert/recover edits (including a file you overwrote), tell them to run `/rewind` (or `/undo` for the last turn) to restore the working tree — checkpoints cover files you edited even in .gitignored dirs. You cannot run `/rewind` yourself; surface it as the recovery path.\n' +
    'When working with a knowledge base or document workspace: use `RepoMap` with scope "docs" to see all document headings and summaries (orient first); use `MemorySearch` to find memories by content when you don\'t know the exact slug; use `Recall` with multiple slugs to batch-load related facts in one call. Prefer targeted loads over reading everything — orient → search → load → act.';
  tools: AgentTool[] = defaultTools();
  maxSteps = 25;
  // --- automatic kill-switches (always on; protect the API budget against runaway loops/abuse) ---
  /** Hard ceiling on accumulated tokens (prompt+completion) across the run; cache READS count at 0.1×
   *  (their real price) so a fat cached context doesn't trip the guard on healthy turns. 0 = unbounded. */
  maxTokens = 200_000;
  /** Idle ceiling in ms: max time since the last productive step before the run is killed (a long/active
   *  step resets it — see runLoop's `lastProgressAt`). Not a whole-run wall clock. 0 = unbounded. */
  timeoutMs = 300_000;
  /** Idle-stall watchdog for a SINGLE streamed model request: abort it if no chunk (text OR reasoning)
   *  arrives for this long. The between-steps `timeoutMs` can't preempt an in-flight `chat()`, so a
   *  provider that goes silent mid-stream would otherwise park the loop forever. 0 = off. */
  stallMs = 60_000;
  /** Stop if the identical tool-call batch (name+args) repeats this many times in a row. 0 = off. */
  maxRepeats = 3;
  /** Cumulative cap on tool calls dispatched across the run. 0 = unbounded. */
  maxToolCalls = 100;
  /** External cancellation — abort the loop between steps (e.g. a UI "Cancel" button). */
  signal?: AbortSignal;
  /** Retry budget for an EMPTY/silent-turn (a delegated-runtime wedge: cursor's warm helper spins then
   *  returns a content-less end, surfaced as `code:'empty'`/`code:'cursor_silent'`). Tuned WIDER than the
   *  network-drop budget (fixed 2) because clearing a wedge needs several reap+respawn cycles — each retry
   *  hits a FRESH cursor session (the adapter evicts the wedged warm helper on the silent-end). Still bounded:
   *  after exhaustion the turn surfaces `finishReason:'empty'` (non-zero exit), never infinite, never silent.
   *  Env: AGENTX_EMPTY_RETRIES. */
  emptyRetries = envInt('AGENTX_EMPTY_RETRIES', 4);
  /** Base backoff (ms) between empty-turn retries; grows linearly per attempt (longer than the network
   *  class's fixed 1s so a wedged helper has time to be reaped/respawned). Env: AGENTX_EMPTY_RETRY_BASE_MS. */
  emptyRetryBaseMs = envInt('AGENTX_EMPTY_RETRY_BASE_MS', 1500);
  /** 0 = never trim. Otherwise cap the messages sent per turn (system + most recent). */
  maxContextMessages = 0;
  /** Note-taking: keep the most-recent N tool-result outputs verbatim; collapse OLDER ones to a one-line
   *  stub in the sent context (the model already consumed them — it can re-Read/re-run). The stored
   *  transcript is never mutated. 0 = keep all verbatim. */
  keepToolOutputs = 0;
  /** How many of the most-recent image-bearing messages get their image REF expanded to real base64 in
   *  the outgoing request. Older ones (and legacy inline base64) go on the wire as the elision stub. */
  keepRecentImages = 1;
  /** Token-aware backstop (~4 chars/token estimate). After note-taking, drop oldest messages from the
   *  sent context until the estimate is under this ceiling (pairing-safe). 0 = off. */
  maxContextTokens = 0;
  /** The MODEL's context window (tokens) — what the context % is measured against and what `/compact`
   *  targets 50% of. Filled by the CLI from model metadata; 0 = fall back to 200k. Deliberately NOT
   *  `maxTokens`: that is the cumulative SPEND kill-switch, and a spend budget must never be able to
   *  drive destructive compaction (`--max-tokens 50000` would otherwise shred the transcript). */
  contextWindow = 0;
  /** Pagination ceiling for a SINGLE tool result (bytes). A result over this is cropped to page 1 with
   *  a marker telling the model it was cropped (refine the query, or page further). Guards against one
   *  Grep/Read/MCP call blowing the whole context window. 0 = off. Default 60k (~15k tokens). */
  maxToolResultBytes = 60_000;
  /** Hook to handle an oversized tool result instead of the default lossy crop: receives the FULL output
   *  and returns the (cropped) string to put in context — e.g. spill to scratch and return a recoverable,
   *  paginated stub. Called only when a result exceeds `maxToolResultBytes`. */
  capToolResult?: (full: string, info: { tool: string; args: any }) => string | Promise<string>;
  /** Browser-run adaptive trimming. Browser MCP tools (`mcp__*browser*`) return huge per-call results
   *  (full DOM text + screenshots) that accumulate per step and blow the cumulative `maxTokens` budget
   *  kill-switch mid-browse. The fix attacks PER-STEP TOKEN GROWTH, and engages ONLY once a browser tool
   *  is ACTUALLY invoked (not on mere config presence — so normal coding is untouched):
   *   - `resultBytes`: tighter `maxToolResultBytes` applied to BROWSER tool results (each oversized DOM is
   *     cropped/spilled to a small stub → small fresh-token tail per step).
   *   - `keepOutputs`: keep only the most-recent N tool-result bodies verbatim ONCE a browser tool has run
   *     (older huge DOMs collapse to one-line stubs → small cached prefix → small 0.1×cacheRead/step).
   *  Explicit `keepToolOutputs`/`maxToolResultBytes` are NOT overridden below their browser values — the
   *  tighter of (user, browser) wins, never loosening a user's cap. Unset = off (no adaptive behavior). */
  browserTrim?: { resultBytes?: number; keepOutputs?: number };
  /** VFS dir(s) of skills (`<dir>/<id>/SKILL.md`). If set: inject a catalog + add the `Skill` tool. Multiple dirs are merged (first wins on name collisions). */
  skillsDir?: string | string[];
  /** VFS dir(s) of slash-command templates (`<dir>/<name>.md`). If set: inject a catalog + add the `SlashCommand` tool. Multiple dirs are merged (first wins). */
  commandsDir?: string | string[];
  /** VFS dir(s) of memory (`<dir>/MEMORY.md`). If set: inject the index at run start (persistence = backend).
   *  Multiple dirs are merged (reads search all; writes go to first). */
  memoryDir?: string | string[];
  /** User-scope memory dir for global facts (type=user/feedback). Remember routes by type when set. */
  memoryUserDir?: string;
  /** Filenames to discover as project instructions (e.g. `AGENT.md`, `AGENTS.md`, `CLAUDE.md`).
   *  Walks the VFS tree and merges all found files (general → specific, like Claude Code).
   *  `true` (default) = auto-discover standard names. `string[]` = custom names. `false` = skip. */
  instructionFiles: boolean | string[] = true;
  /** Host interaction channel (human-in-the-loop). If set: adds the `AskUserQuestion` tool. */
  host?: HostBridge;
  /** Add the `AskUserQuestion` tool when a host is present (default true). Set false for an agent that
   *  must never block a turn on a structured question — e.g. a voice reflex that confirms inline. */
  askUserQuestion = true;
  /** Deterministic interception points around tool execution (pre/post/stop). */
  hooks?: Hooks;
  /** If true: add the `Task` tool so the agent can spawn depth-limited child agents over the VFS. */
  subagents = false;
  /** VFS dir of typed-subagent defs (`<dir>/<name>.md`). If set with `subagents`: inject a catalog + enable the `Task` `agentType` param. */
  agentsDir?: string;
  /** Current recursion depth (0 = top-level); spawned children run at depth+1. */
  depth = 0;
  /** Hard ceiling on subagent nesting (beyond it the `Task` tool refuses to spawn). */
  maxDepth = 2;
  /** Real-disk dir where child agents stream their transcript as they run (origin-anchored — survives
   *  worktree teardown / a child's own cwd). When set, an interrupted/failed child commits a STATUS HINT
   *  + a pointer to its trace instead of a dangling tool_call, so the parent can read it and decide
   *  whether to ignore, pick it up, or restart. Unset (library/sandbox default) = no tracing. */
  traceDir?: string;
  /** Stream tokens from the model. Takes effect only with a `host.notify`; off => current (non-stream) behavior. */
  stream = false;
  /** Closure reflex for delegated runtimes (cursor/claude-code): when such a turn ends on a tool call with
   *  no assistant text after it (the runtime went silent post-work), nudge ONE more step to ANSWER the user
   *  from the tool results already in the transcript — so the turn delivers a reply instead of trailing off
   *  mid-narration. Deliberately not a "summarize what you did" nudge: that produced process reports and
   *  permission-asking instead of answers (see the nudge text in runLoop).
   *  Fires at most once per run and only for delegated runtimes (native turns never emit the trigger). */
  closeDelegatedTurns = true;
  /** Fold the dropped middle of an over-long transcript into a synthetic summary (edge-safe, no LLM). Off => drop-oldest. */
  compaction?: { maxMessages: number };
  /** Add `Checkpoint`/`Rollback` tools (requires the fs to be an OverlayFilesystem). */
  checkpoints = false;
  /** Enable `bash({background:true})` + JobOutput/JobStatus/JobKill — sandbox background jobs (overlay-isolated,
   *  committed on completion, drained at turn end). Useful when the VFS backend is slow (remote) or for sub-agents. */
  backgroundJobs = false;
  /** Plan mode: block mutating tools until the agent calls `ExitPlanMode` (host-approved). */
  planMode = false;
  /** Permission policy gating each tool call (allow / ask / deny). */
  permissions?: PermissionPolicy;
  /** Opt-in syntax guardrail: refuse to persist a syntactically-broken code-file write/edit. Default off. */
  lintOnWrite?: boolean;
  /** Optional PDF text extraction for Read on .pdf files (node hosts wire pdftotext); absent => Read explains. */
  pdfText?: (path: string) => Promise<string>;
  /** Opt-in: after a write-class tool runs, run `command` over the VFS and append any failure to the tool result.
   *  `tools` defaults to ['Write','Edit','MultiEdit','ApplyEdits']. */
  autoTest?: { command: string; tools?: string[] };
  /** Provider-specific options forwarded to ai.chat() (e.g. cursor mcpServers, cwd). */
  providerOptions?: Record<string, unknown>;
  /** Prompt caching (providers that support it, e.g. Anthropic): cache tools/system/conversation
   *  prefix across the loop's steps — reads cost 0.1x, writes 1.25x. A multi-step agent loop
   *  re-sends its whole prefix every step, so this is a large net cost cut. Default on. */
  promptCache = true;
  /** Tool selection mode: 'auto' = model decides (needed for Groq); undefined = provider default. */
  toolChoice?: 'auto' | 'required' | 'none';
  /** Extended-thinking / reasoning effort, normalized across providers (anthropic, openai).
   *  `'off'`/undefined = none; `'low'|'medium'|'high'` or a raw token budget. Mapped to the
   *  provider-specific request shape via {@link reasoningToChatFragment}; explicit `providerOptions` wins. */
  reasoning?: ReasoningEffort;
  /** Seed a fresh `run()` with prior conversation turns (real role/content messages) so the model
   *  sees history as structured turns instead of a flattened text blob. They are inserted AFTER the
   *  stable system prompt and BEFORE the current user turn — so the cached system(+tools) prefix never
   *  shifts. Turns are normalized before sending (empties dropped, adjacent same-role coalesced, and the
   *  first tail turn forced to `user`) so the transcript is always a valid Anthropic alternation ending
   *  at the current user prompt. Unset/empty ⇒ current behavior, byte-for-byte. */
  priorMessages?: Array<{ role: 'user' | 'assistant'; content: MessageContent }>;
}

/** Normalize seed history + the live prompt into a valid alternating tail (the messages that follow the
 *  system turn). Drops empties, coalesces adjacent same-role turns, guarantees the first turn is `user`
 *  (Anthropic requires the first non-system message to be user) and that the sequence ends at `current`.
 *  Empty `prior` ⇒ `[{ user: current }]` — identical to the pre-priorMessages transcript. */
function seedTranscriptTail(
  prior: Array<{ role: 'user' | 'assistant'; content: MessageContent }> | undefined,
  current: MessageContent,
): Message[] {
  const seq: Message[] = (prior ?? [])
    .filter((m) => contentText(m.content).trim().length > 0)
    .map((m) => ({ role: m.role, content: m.content }));
  seq.push({ role: 'user', content: current });
  const out: Message[] = [];
  for (const m of seq) {
    const last = out[out.length - 1];
    if (last && last.role === m.role) {
      if (typeof last.content === 'string' && typeof m.content === 'string') {
        last.content = `${last.content}\n\n${m.content}`; // merge same-role text turns
        continue;
      }
      // multimodal boundary (can't concat) — insert a neutral opposite-role spacer to keep alternation
      out.push({ role: m.role === 'user' ? 'assistant' : 'user', content: '(continuing)' });
    }
    out.push({ role: m.role, content: m.content });
  }
  if (out.length && out[0].role !== 'user') out.unshift({ role: 'user', content: '(Earlier conversation)' });
  return out;
}

/**
 * The agentic loop: chat() -> dispatch tool_calls over the VFS -> thread results
 * back -> repeat until the model stops (no tool calls) or maxSteps is hit.
 */
export class Agent {
  public options: AgentOptions;
  public transcript: Message[] = [];
  private ctx!: ToolContext; // built in the ctor when `fs` is provided, else lazily in ensureFs()
  private lastTrimNotified = 0; // last auto-trim drop count surfaced via host.notify (dedup)
  private activeTools: AgentTool[] = [];
  private activeHooks?: Hooks; // composed: user hooks + plan-mode + permissions
  private prepared = false;        // memo guard: prompt/tools/plan-state built once per conversation
  private systemPromptCache = '';  // the assembled system prompt from the last prepare()
  private started = false;         // session-start lifecycle hook fires once per conversation
  private parkedMs = 0;            // cumulative time blocked on the HUMAN (permission/plan prompts) — excluded from the timeout
  private browserActive = false;   // flips true once a browser MCP tool is actually invoked → adaptive trimming engages (browserTrim)

  /** Time a human-blocking await (a permission/plan prompt) and bank it in `parkedMs` so idle prompt
   *  time never trips the wall-clock kill-switch. The agent did no work while parked on the user. */
  private async park<T>(p: Promise<T>): Promise<T> {
    const t = Date.now();
    try { return await p; } finally { this.parkedMs += Date.now() - t; }
  }

  /** Force the next `send()`/`run()` to rebuild the system prompt, tools, plan-mode and permission hooks
   *  from `options` — apply mid-conversation changes to `planMode`/`permissions`/`model` etc. (prepare()
   *  is otherwise memoized per conversation). */
  reprepare(): void { this.prepared = false; }

  /** Tools injected via addTools(); kept separate from options.tools so prepare() rebuilds don't drop them. */
  private injectedTools: AgentTool[] = [];

  /** Current tool surface for hosts/UIs (configured + injected, deduped by name) — options.tools
   *  alone misses addTools()-injected ones (e.g. MCP servers mounted after startup). */
  get toolNames(): string[] {
    return [...new Set([...(this.options.tools ?? []), ...this.injectedTools].map((t) => t.name))];
  }

  /** Inject tools into a running agent (e.g. dynamically mounted MCP servers). Takes effect on the next turn
   *  and survives prepare() rebuilds (reprepare(), new conversations). */
  addTools(tools: AgentTool[]): void {
    this.injectedTools.push(...tools);
    this.activeTools.push(...tools);
  }

  /** Remove tools by name from a running agent. Returns the count removed. Also filters
   *  `options.tools` — otherwise a constructor-passed tool resurrects on the next prepare()
   *  rebuild (and re-adding it via addTools() would then DUPLICATE it in the wire schema). */
  removeTools(names: Set<string> | string[]): number {
    const s = names instanceof Set ? names : new Set(names);
    const before = this.activeTools.length;
    this.activeTools = this.activeTools.filter((t) => !s.has(t.name));
    this.injectedTools = this.injectedTools.filter((t) => !s.has(t.name));
    if (this.options.tools) this.options.tools = this.options.tools.filter((t) => !s.has(t.name));
    return before - this.activeTools.length;
  }

  /** Messages the host queued to steer the RUNNING turn (Claude-Code-style mid-turn input). The loop
   *  drains them at each step boundary — after tool results are appended, before the next model call —
   *  so they ride along with the latest tool results. A no-op between turns (nothing drains it). */
  private pendingInjections: MessageContent[] = [];

  /** Queue a user message into the in-flight turn. It lands as a `user` message at the next step
   *  boundary; if the model was about to stop, the injection re-opens the loop so it's never lost.
   *  Pushing onto `transcript` directly from outside would race the loop — this drains inside it. */
  inject(content: MessageContent): void { this.pendingInjections.push(content); }

  /** Drop any queued-but-unconsumed injections. The REPL calls this after a LOCAL command (/cmd, !bash)
   *  — which runs no send() to drain them — so a mid-command steer can't leak into the next real turn. */
  clearInjections(): void { this.pendingInjections.length = 0; }

  /** Pull queued injections that are plain TEXT, removing them, for a runtime that can accept mid-turn
   *  steers over its own channel (claude-code's warm InputQueue via `providerOptions.steer`). Non-text
   *  injections (images) stay queued for the step-boundary `drainInjections()` re-open — the text-only
   *  steer channel can't carry them. Consumed DURING chat(); no overlap with drainInjections() (post-call). */
  drainPendingSteers(): string[] {
    if (!this.pendingInjections.length) return [];
    const texts: string[] = [];
    this.pendingInjections = this.pendingInjections.filter((c) => {
      if (typeof c === 'string') { texts.push(c); return false; }
      return true; // keep non-text (image parts) for the transcript-injection path
    });
    return texts;
  }

  /** Fold any host-injected messages into the transcript as user turns. Returns true if any landed. */
  private drainInjections(): boolean {
    if (!this.pendingInjections.length) return false;
    for (const content of this.pendingInjections.splice(0)) this.transcript.push({ role: 'user', content });
    return true;
  }

  constructor(options?: Partial<AgentOptions>) {
    this.options = { ...new AgentOptions(), ...options } as AgentOptions;
    if (this.options.fs) this.buildCtx(); // fs provided → build the tool context now (sync, unchanged behavior)
  }

  /** Build the tool context from the resolved fs + options. Idempotent-safe: called once (ctor or ensureFs). */
  private buildCtx(): void {
    this.ctx = makeContext(this.options.fs!, this.options.host);
    this.ctx.signal = this.options.signal; // expose run-cancellation to abort-aware tools (e.g. real shell)
    if (this.options.lintOnWrite) this.ctx.lint = checkSyntax; // opt-in syntax guardrail
    if (this.options.pdfText) this.ctx.pdfText = this.options.pdfText; // host-wired PDF text extraction (Read on .pdf)
    this.ctx.ai = this.options.ai; // expose model to tools that run their own LLM pass (e.g. Review)
    this.ctx.model = this.options.model;
    this.ctx.parkHuman = (p) => this.park(p); // route interactive tools' human-waits into the parked-time accumulator
    if (this.options.backgroundJobs) this.ctx.jobs = new SandboxJobRegistry(); // enables bash({background:true})
  }

  /**
   * Resolve the filesystem + build the tool context if the ctor couldn't (no `fs` was passed). The disk
   * default lives HERE, not in the ctor: the ctor can't await, and we must avoid a STATIC `node:fs` import
   * in the core (edge/browser would break). The dynamic import only fires when `fs` is omitted — edge code
   * always passes its own `MemFilesystem`, so the node module is never reached. Default = jailed disk @ cwd.
   */
  private async ensureFs(): Promise<void> {
    if (this.ctx) return; // already built (fs was provided to the ctor, or a prior run resolved it)
    if (!this.options.fs) {
      const { NodeDiskFilesystem } = await import('./NodeDiskFilesystem');
      const { JailedFilesystem } = await import('./JailedFilesystem');
      const disk = new NodeDiskFilesystem(process.cwd());
      await disk.init();
      this.options.fs = new JailedFilesystem(disk); // DEFAULT_DENY hides secrets out of the box
      log.info(`no fs provided — defaulting to jailed real disk at ${process.cwd()}`);
    }
    this.buildCtx();
  }

  /**
   * Assemble the system prompt + active tools/hooks ONCE per conversation (memoized).
   * `run()` resets the memo to start fresh; `send()` reuses it, so multi-turn state —
   * plan-mode approval, the tool set, the skills/commands/memory snapshot — stays stable
   * across turns (and the frozen prompt pairs well with prompt caching). Does NOT touch
   * the transcript; `run`/`send` own that.
   */
  private async prepare(taskHint?: string): Promise<string> {
    if (this.prepared) return this.systemPromptCache;
    const o = this.options;
    const fs = o.fs!; // resolved by ensureFs() before any run()/send() reaches prepare()
    let systemPrompt = o.systemPrompt;
    let tools = o.tools;
    if (o.instructionFiles !== false) {
      const names = Array.isArray(o.instructionFiles) ? o.instructionFiles : undefined;
      const ins = await loadInstructions(fs, names);
      if (ins) systemPrompt += '\n\n' + ins;
    }
    if (o.memoryDir) {
      // taskHint floats the most task-relevant learnings to the top of a large index; the rest
      // collapse to slugs (still Recall-able) — bounds tokens as learnings accumulate over time.
      const { index, tools: memTools } = await loadMemory(fs, o.memoryDir, { relevanceHint: taskHint, userDir: o.memoryUserDir });
      if (index) systemPrompt += '\n\n' + index; // hot: compact (ranked) index always injected
      tools = [...tools, ...memTools]; // cold: Recall (pull fact) + Remember (persist fact)
    }
    // Merged slash namespace (CC parity): each tool cross-resolves into the other registry on a
    // miss, so `/x` works whether x is a command or a skill regardless of which tool the model picks.
    const skillsDir = o.skillsDir, commandsDir = o.commandsDir;
    if (skillsDir) {
      // taskHint floats the most relevant skills to the top of a large catalog; full set stays loadable by name.
      const sibling = commandsDir
        ? async (name: string) => {
            const c = (await scanCommands(fs, commandsDir)).find((x) => x.name === name);
            return c ? { path: c.path, stripFrontmatter: true } : undefined;
          }
        : undefined;
      const { catalog, tool } = await loadSkills(fs, skillsDir, { relevanceHint: taskHint, sibling });
      if (catalog) systemPrompt += '\n\n' + catalog;
      if (tool) tools = [...tools, tool];
    }
    if (commandsDir) {
      const sibling = skillsDir
        ? async (name: string) => {
            const s = (await scanSkills(fs, skillsDir)).find((x) => x.name === name);
            return s ? { path: s.path, stripFrontmatter: false } : undefined;
          }
        : undefined;
      const { catalog, tool } = await loadCommands(fs, commandsDir, { relevanceHint: taskHint, sibling });
      if (catalog) systemPrompt += '\n\n' + catalog;
      if (tool) tools = [...tools, tool];
    }
    if (o.host && o.askUserQuestion) tools = [...tools, askUserQuestionTool];
    if (o.subagents) {
      let agents: Awaited<ReturnType<typeof loadAgents>>['agents'] | undefined;
      if (o.agentsDir) {
        const loaded = await loadAgents(fs, o.agentsDir);
        agents = loaded.agents;
        if (loaded.catalog) systemPrompt += '\n\n' + loaded.catalog;
      }
      const taskOpts = { ai: o.ai, model: o.model, fs, depth: o.depth, maxDepth: o.maxDepth, agents, hooks: o.hooks, traceDir: o.traceDir };
      tools = [...tools, makeTaskTool(taskOpts), makeTaskBatchTool(taskOpts)];
    }
    if (o.checkpoints) tools = [...tools, ...checkpointTools()];
    if (this.ctx.jobs) tools = [...tools, ...makeJobTools(this.ctx.jobs)]; // JobOutput/JobStatus/JobKill
    const plan = o.planMode ? planMode({ host: o.host }) : undefined; // gates mutating tools until approved
    if (plan) tools = [...tools, plan.tool];
    this.activeHooks = composeHooks(o.hooks, plan?.hooks, o.permissions?.hooks());
    this.activeTools = [...tools, ...this.injectedTools]; // injected (addTools) survive the rebuild
    this.systemPromptCache = systemPrompt;
    this.prepared = true;
    return systemPrompt;
  }

  /** Single-shot: reset all per-conversation state and run `task` to completion. `task` may be plain
   *  text or multimodal content parts (text + images). */
  async run(task: MessageContent): Promise<RunResult> {
    await this.ensureFs();  // resolve the disk default (if no fs was passed) before prepare reads o.fs
    this.prepared = false; // fresh conversation → rebuild prompt + tools + plan-mode state
    this.started = false;
    const systemPrompt = await this.prepare(contentText(task)); // task text hints catalog relevance
    const startCtx = await this.fireSessionStart();      // [lifecycle] once-per-session context injection
    const userContent = await this.applyPromptSubmit(task); // [lifecycle] per-turn prompt rewrite (text only)
    this.transcript = [
      { role: 'system', content: systemPrompt + (startCtx ? '\n\n' + startCtx : '') },
      // Prior turns (if any) sit between the stable system prefix and the live user turn — the cached
      // system(+tools) prefix is unaffected; normalization keeps the alternation valid.
      ...seedTranscriptTail(this.options.priorMessages, userContent),
    ];
    return this.runLoop();
  }

  /** Apply onUserPromptSubmit to a turn: rewrite plain text; pass multimodal content through untouched. */
  private async applyPromptSubmit(task: MessageContent): Promise<MessageContent> {
    return typeof task === 'string' ? await this.fireUserPromptSubmit(task) : task;
  }

  /** Fire onSessionStart once per conversation; returns any injected context. */
  private async fireSessionStart(): Promise<string | undefined> {
    if (this.started) return undefined;
    this.started = true;
    const ctx = await this.activeHooks?.onSessionStart?.();
    return typeof ctx === 'string' && ctx ? ctx : undefined;
  }

  /** Fire onUserPromptSubmit; returns the (possibly rewritten) prompt text. */
  private async fireUserPromptSubmit(task: string): Promise<string> {
    const r = await this.activeHooks?.onUserPromptSubmit?.(task);
    return typeof r === 'string' ? r : task;
  }

  /**
   * Multi-turn: continue the existing conversation by appending a user turn instead of
   * resetting — this is what makes an interactive REPL feel like one conversation. The
   * leading system message is (re)synced to the current prompt, so a resumed session whose
   * tools were rebuilt gets a matching catalog rather than a stale one.
   */
  async send(task: MessageContent): Promise<RunResult> {
    await this.ensureFs();  // resolve the disk default (if no fs was passed) before prepare reads o.fs
    const systemPrompt = await this.prepare(contentText(task)); // first turn's task hints catalog relevance; frozen thereafter (memoized → cache-stable)
    const startCtx = await this.fireSessionStart();      // [lifecycle] first send() of a fresh agent counts as session start
    const userContent = await this.applyPromptSubmit(task);
    const sys = systemPrompt + (startCtx ? '\n\n' + startCtx : '');
    if (this.transcript[0]?.role === 'system') this.transcript[0] = { role: 'system', content: sys };
    else this.transcript.unshift({ role: 'system', content: sys });
    this.transcript.push({ role: 'user', content: userContent });
    return this.runLoop();
  }

  /**
   * Fold the conversation in place (manual `/compact`) until it fits a TOKEN budget — not a message
   * count. Message-count folding alone frees almost nothing when a few recent tool results are huge,
   * so we escalate: fold the middle into a synthetic recap → elide oversized tool-result bodies in the
   * retained window → halve the window → drop-oldest backstop. Deterministic, no LLM call.
   * `focus` (e.g. `/compact keep the API details`) preserves matching lines from the dropped span verbatim.
   * Returns before/after token estimates so the caller can report honestly (0 freed => say so).
   */
  compactNow(maxMessages = 12, focus?: string): CompactResult {
    const cap = Agent.contextCap(this.options);
    const target = Math.round(cap * Agent.COMPACT_TARGET);
    // Budget on the WIRE view (same estimator as /context + the footer) — see estimateTokens.
    const kr = this.options.keepRecentImages ?? 1;
    const tokensBefore = estimateTokens(this.transcript, kr);
    const msgsBefore = this.transcript.length;
    let max = Math.max(2, maxMessages);
    // Nothing to fold AND already under budget: a genuine no-op. Return BEFORE onPreCompact — hooks that
    // persist a summary must not write a spurious one for a `/compact` that changes nothing.
    if (this.transcript.length <= max && tokensBefore <= target)
      return { tokensBefore, tokensAfter: tokensBefore, target, cap, removed: 0 };
    void this.activeHooks?.onPreCompact?.(this.transcript); // [lifecycle] observe before folding (fire-and-forget)
    let out = this.transcript;
    if (out.length > max) out = compact(out, max, focus);
    // Inline base64 can't be shaved, only dropped — so drop the whales FIRST. Otherwise a single oversized
    // screenshot is paid for by shredding cheap text messages further down the ladder.
    // The NEWEST image is spared on this pass: `releaseStaleImages(keepRecent = 1)` deliberately keeps it,
    // and a user who pastes a screenshot and then runs `/compact` must still be able to ask about it.
    if (estimateTokens(out, kr) > target) out = elideOversizedImages(out, target, true, kr);
    if (estimateTokens(out, kr) > target) out = capOversizedBodies(out, target, kr);
    // Only once the text ladder has done all it can does the newest image become fair game.
    if (estimateTokens(out, kr) > target) out = elideOversizedImages(out, target, false, kr);
    // Still over (e.g. one giant user paste / assistant reply): halve the window, re-capping each pass.
    while (estimateTokens(out, kr) > target && max > 2 && out.length > 2) {
      max = Math.max(2, Math.floor(max / 2));
      out = capOversizedBodies(compact(out, max, focus), target, kr);
    }
    if (estimateTokens(out, kr) > target) out = fitTokenBudget(out, target, kr); // drop-oldest backstop
    out = dropOrphanToolResults(out); // never leave a tool_result whose tool_call was folded away
    this.transcript = out;
    this.releaseStaleImages(); // stale inline base64 is dead weight in the folded transcript too
    // Measure the transcript we actually kept (releaseStaleImages may have shrunk it further).
    return { tokensBefore, tokensAfter: estimateTokens(this.transcript, kr), target, cap, removed: msgsBefore - this.transcript.length };
  }

  /** Drop inline base64 images from older messages so Bun/JSC can reclaim the heap (see contentBytes). */
  releaseStaleImages(keepRecent = 1): void {
    const next = elideStaleImages(this.transcript, keepRecent);
    if (next !== this.transcript) this.transcript = next;
  }

  private async runLoop(): Promise<RunResult> {
    const o = this.options;
    const wireTools = toWireTools(this.activeTools);
    // Stream only when explicitly requested AND a host can render deltas; else stay on the non-stream path.
    const useStream = o.stream === true && typeof o.host?.notify === 'function';
    let steps = 0;
    const usage = { promptTokens: 0, completionTokens: 0, totalTokens: 0, cacheCreationTokens: 0, cacheReadTokens: 0 };
    let usageEstimated = false; // flips true the first turn a provider returns no usage and we estimate
    const start = Date.now();
    this.parkedMs = 0;       // reset per run: only THIS run's human-wait is excluded from the timeout
    this.ctx.needsInput = undefined; // reset per run: a question answered last run must not re-stop this one
    let toolCallsTotal = 0;  // cumulative tool calls dispatched
    let lastFp = '';         // fingerprint of the previous tool-call batch
    let repeats = 0;         // consecutive identical batches
    let closureNudged = false; // closeDelegatedTurns: the closure reflex fires at most once per run
    // Progress clock: `timeoutMs` bounds IDLE time (since last productive step), not total run length —
    // a single extended-thinking step can legitimately exceed the whole-run ceiling while actively
    // streaming reasoning (stallMs keeps that in-flight turn alive), so an absolute wall-clock would kill
    // completed, productive work. Runaway-but-active loops stay covered by maxSteps/maxToolCalls/maxRepeats/budget.
    let lastProgressAt = start;
    // Single exit for every kill-switch: warn + return a result carrying the reason.
    const kill = (finishReason: RunResult['finishReason']): RunResult => {
      log.warn(`kill-switch: ${finishReason} (steps=${steps}, tokens=${usage.totalTokens}, budgetTokens=${Math.round(usage.totalTokens - 0.9 * usage.cacheReadTokens)}, ms=${Date.now() - start - this.parkedMs}${finishReason === 'timeout' ? `, idle=${Date.now() - lastProgressAt - this.parkedMs}ms` : ''}${this.parkedMs ? ` +${this.parkedMs} parked` : ''})`);
      this.ctx.jobs?.killAll(); // abort background jobs (don't commit) — a budget/timeout kill shouldn't leak writes
      return { text: lastAssistantText(this.transcript), steps, finishReason, messages: this.transcript, usage, usageEstimated };
    };

    while (true) {
      if (o.signal?.aborted) return kill('aborted');
      if (steps >= o.maxSteps) return kill('max_steps');
      if (o.timeoutMs && Date.now() - lastProgressAt - this.parkedMs >= o.timeoutMs) return kill('timeout'); // idle-since-last-progress: an active/long step resets the clock; parked prompt time excluded
      // Budget = cost proxy, not raw volume: cache reads are ~10% of fresh-token price, and a fat cached
      // context (MCP schemas + big tool results) re-counts every step — at full weight a healthy ~25k-ctx
      // session dies near step 8. Weighting reads at 0.1 keeps runaway protection (fresh tokens count full).
      const budgetTokens = usage.totalTokens - 0.9 * usage.cacheReadTokens;
      if (o.maxTokens && budgetTokens >= o.maxTokens) return kill('budget');
      steps++;
      this.options.host?.notify?.({ kind: 'turn_start', message: `step ${steps}` });

      let res: ChatResponse;
      const sent = this.trimContext(); // the exact context sent this turn (reused for usage estimation)
      // Normalized reasoning → provider request shape; explicit providerOptions overrides the derived one.
      const frag = reasoningToChatFragment(o.model, o.reasoning);
      // claude-code/* is an autonomous runtime (Claude Agent SDK) that runs its OWN tools inside its
      // subprocess and rejects host-defined tool schemas — send none. External tools reach it via
      // providerOptions.mcpServers (wired by the CLI's claudeCodeProviderOptions). Mirror of cursor below.
      const sentTools = o.model.startsWith('claude-code/') ? [] : wireTools;
      // Cursor is an agent runtime — bridge host tools via IPC-based toolExecutor callback.
      const isCursorWithTools = o.model.startsWith('cursor/') && wireTools.length > 0;
      const cursorPo = isCursorWithTools ? {
        toolExecutor: async (name: string, args: Record<string, any>) => {
          const tc = { id: `cursor-${Date.now()}`, type: 'function' as const, function: { name, arguments: JSON.stringify(args) } };
          const raw = await this.dispatch(tc);
          return typeof raw === 'string' ? raw : raw.text;
        },
      } : undefined;
      // claude-code warm sessions accept mid-turn steers via a `steer` pull source (the adapter drains it
      // into the SDK's InputQueue). Route text injections through it so they reach the live warm session;
      // non-text injections (and any that land after the last drain) fall through to the step-boundary re-open.
      const claudeSteerPo = o.model.startsWith('claude-code/') ? { steer: () => this.drainPendingSteers() } : undefined;
      const reasonOpts = {
        ...frag,
        ...(o.promptCache ? { promptCache: true } : {}),
        ...(o.providerOptions || cursorPo || claudeSteerPo ? { providerOptions: { ...frag.providerOptions, ...o.providerOptions, ...cursorPo, ...claudeSteerPo } } : {}),
      };
      try {
        // One step = one model call; a TRANSIENT network drop (mid-stream or pre-flight) retries the
        // step from the last committed transcript state instead of failing the whole turn. Server-side
        // transient HTTP errors (5xx/408/overloaded) retry too; 4xx like rate limits are NOT retried
        // here — the AIClient's own rate-limit retry handles those.
        for (let attempt = 0; ; attempt++) {
          try {
            if (useStream) {
              // Guard the whole request (time-to-first-chunk + inter-chunk gaps) with an idle-stall
              // watchdog: if the provider goes silent past o.stallMs, abort THIS request via a linked
              // controller so the loop can retry the step instead of hanging inside `chat()` forever.
              const stall = this.armStallWatchdog(o.signal, o.stallMs);
              try {
                const r = await o.ai.chat({ model: o.model, messages: sent, tools: sentTools, stream: true, signal: stall.signal, ...(o.toolChoice ? { toolChoice: o.toolChoice } : {}), ...reasonOpts });
                res = await this.consumeStream(r as AsyncIterable<StreamChunk>, stall.poke);
              } catch (err) {
                throw stall.reclassify(err); // a watchdog-fired abort → Error{code:'stall'}; parent/user aborts pass through
              } finally {
                stall.clear();
              }
            } else {
              const r = await o.ai.chat({ model: o.model, messages: sent, tools: sentTools, stream: false, signal: o.signal, ...(o.toolChoice ? { toolChoice: o.toolChoice } : {}), ...reasonOpts });
              res = r as ChatResponse;
            }
            // Empty-stop guard: a ZERO-WORK turn — the model went silent on the FIRST call, committing
            // nothing to the transcript, yet downstream would report it as a successful "done" (the real
            // agentx-composer "spins then ✓ done · 0 tools" incident). Scope to `steps === 1` (incremented
            // at the top of the loop, reset per-run): only the first model call can be zero-work, since any
            // later step follows tools/text already committed this turn — an empty wrap-up after real work
            // is a productive turn and must NOT be failed. Throw code:'empty' → the catch below retries it
            // through the SAME transient machinery (bounded by attempt<2); after the retries it propagates
            // to the outer catch which surfaces finishReason:'empty' (a failed turn), never a phantom stop.
            // Skip if a steer/injection is pending — that re-opens the turn naturally at the loop boundary.
            if (steps === 1 && this.isEmptyStop(res) && !this.pendingInjections.length) {
              const e = new Error('model returned an empty turn (finishReason=stop, no text, no tools)') as Error & { code: string };
              e.code = 'empty';
              throw e;
            }
            break;
          } catch (err) {
            const sc = (err as any)?.statusCode;
            const serverSide = sc >= 500 || sc === 408 || /Service Unavailable|overloaded|Internal server error|Bad gateway|Gateway time/i.test(String((err as any)?.message ?? ''));
            const network = !sc && /ECONNRESET|ECONNREFUSED|ETIMEDOUT|ENOTFOUND|EAI_AGAIN|EPIPE|socket hang up|fetch failed|network|terminated|UND_ERR/i.test(String((err as any)?.message ?? (err as any)?.code ?? err));
            const stalled = (err as any)?.code === 'stall'; // watchdog-fired: provider went silent mid-stream
            // Empty/silent turn: agentx's own zero-work guard (code:'empty') OR a delegated runtime signalling
            // a wedged content-less session at the source (cursor adapter's code:'cursor_silent'). Both retry
            // through the SAME machinery on the WIDER empty budget — the adapter reaps the wedged warm helper
            // so each attempt spawns a fresh session.
            const empty = (err as any)?.code === 'empty' || (err as any)?.code === 'cursor_silent';
            // Empty gets its own (wider) attempt cap + backoff; network/server/stall keep the fixed budget of 2.
            const maxAttempts = empty ? Math.max(o.emptyRetries, 0) : 2;
            const transient = !o.signal?.aborted && !isAbortError(err) && attempt < maxAttempts && (network || serverSide || stalled || empty);
            if (!transient) throw err;
            const waitMs = (empty ? o.emptyRetryBaseMs : 1000) * (attempt + 1);
            log.warn(`${empty ? 'empty model turn' : stalled ? 'model stall' : 'network drop'} mid-step (${(err as any)?.message ?? err}) — retrying in ${waitMs}ms`);
            o.host?.notify?.({ kind: 'retry', message: `${empty ? 'model returned nothing' : stalled ? 'model stalled' : 'connection dropped'} — retrying step (#${attempt + 1})` });
            await new Promise((r) => setTimeout(r, waitMs));
          }
        }
      } catch (err) {
        // A budget-exhausted signal (e.g. proxy 429) is a clean stop, not a crash:
        // preserve the transcript so the run can be resumed once the cap is raised.
        if ((err as any)?.code === 'budget') return kill('budget');
        // Watchdog gave up after retries: the provider stayed silent. Clean stop (preserve transcript
        // for resume), labelled 'timeout' so it's diagnosable — not a crash, not a user 'aborted'.
        if ((err as any)?.code === 'stall') { log.warn(`model stalled out after retries: ${(err as any)?.message ?? err}`); return kill('timeout'); }
        // Abort wins over the empty/silent classification: an early barge-in of a cursor turn can surface as
        // a content-less end (code:'cursor_silent'), but a USER-aborted turn is 'aborted', never a failed
        // 'empty'. Check the signal FIRST. (The flag can LAG the throw under a barge-in race — the controller
        // fires, the provider rejects, but o.signal.aborted hasn't settled — so match the abort message too,
        // else a clean cancel is logged as a crash AND its "operation was aborted" string leaks onto the
        // spoken/utterance line.)
        if (o.signal?.aborted || isAbortError(err)) return kill('aborted');
        // Empty-stop after the bounded retries: the model kept returning nothing. Surface it as a failed
        // turn (finishReason:'empty' → the host prints ✗ / exits non-zero) instead of a phantom "done".
        if ((err as any)?.code === 'empty' || (err as any)?.code === 'cursor_silent') { log.warn(`model returned empty/silent turns after retries — surfacing as failed`); return { text: '', steps, finishReason: 'empty', messages: this.transcript, usage, usageEstimated, error: err }; }
        // surface the provider's human message (non-enumerable → lost by JSON.stringify); the bare
        // {code,statusCode} alone reads as a malformed request when it's often billing/quota (e.g. a 400
        // whose body is "credit balance is too low"). The raw response body carries that detail —
        // extract it onto the error so hosts/logs see WHY, not just the status code.
        const body = (err as any)?.body ?? (err as any)?.response?.data ?? (err as any)?.error;
        // try/catch: a circular body must not crash the error handler itself
        let bodyStr: string | undefined;
        try { bodyStr = body && typeof body !== 'string' ? JSON.stringify(body).slice(0, 2000) : (body as string | undefined); } catch { bodyStr = undefined; }
        if (bodyStr && err instanceof Error && !err.message.includes(bodyStr)) (err as any).detail = bodyStr;
        // Error properties are non-enumerable → JSON.stringify(err) = {} — serialize explicitly so logs are useful
        const errLog = err instanceof Error ? { message: err.message, ...((err as any).detail ? { detail: (err as any).detail } : {}), stack: err.stack } : err;
        log.error(`chat() failed: ${(err as any)?.message ?? err}${bodyStr ? ` — ${bodyStr}` : ''}`, errLog);
        return { text: '', steps, finishReason: 'error', messages: this.transcript, usage, usageEstimated, error: err };
      }
      // Cancelled mid-response → end the turn as 'aborted', not as a clean 'stop' on the partial reply.
      if (o.signal?.aborted) return kill('aborted');

      // ai.libx.js streaming doesn't surface token usage — estimate (~4 chars/token) so cost reporting
      // AND the maxTokens budget kill-switch still work when streaming (the CLI default). Non-stream
      // responses carry exact usage and are left untouched.
      if (!res.usage) {
        const promptTokens = estimateTokens(sent);
        const completionTokens = Math.ceil((contentText(res.content).length + (res.toolCalls ? JSON.stringify(res.toolCalls).length : 0)) / 4);
        res.usage = { promptTokens, completionTokens, totalTokens: promptTokens + completionTokens };
        usageEstimated = true;
      }
      if (res.usage) {
        usage.promptTokens += res.usage.promptTokens ?? 0; usage.completionTokens += res.usage.completionTokens ?? 0; usage.totalTokens += res.usage.totalTokens ?? 0;
        usage.cacheCreationTokens += (res.usage as any).cacheCreationTokens ?? 0; usage.cacheReadTokens += (res.usage as any).cacheReadTokens ?? 0;
      }
      // A delegated runtime (cursor/claude-code) ran its tools inside its own loop — they reach us only
      // as display-only toolActivity, never the transcript, leaving it narration-only (non-auditable and
      // blind on resume). Reconstruct assistant(tool_calls)+tool pairs so the transcript matches a native
      // turn's shape. Output trimmed via the SAME cap+cropResult path native results use. Pushed BEFORE
      // the final text (the runtime's summary came after its tools).
      const delegatedTools = res.delegatedTools ?? [];
      if (delegatedTools.length) {
        const cap = this.options.maxToolResultBytes ?? 0;
        this.transcript.push({
          role: 'assistant',
          content: '',
          tool_calls: delegatedTools.map((a) => ({ id: a.id, type: 'function' as const, function: { name: a.name || 'tool', arguments: (() => { try { return JSON.stringify(a.input ?? {}); } catch { return '{}'; } })() } })),
        });
        for (const a of delegatedTools) {
          const out = typeof a.output === 'string' ? a.output : a.output == null ? '' : (() => { try { return JSON.stringify(a.output); } catch { return String(a.output); } })();
          this.transcript.push({ role: 'tool', tool_call_id: a.id, content: cap > 0 && out.length > cap ? cropResult(out, cap) : out });
        }
      }

      const toolCalls = res.toolCalls ?? [];
      // A turn with no tool calls AND no text is dead air — don't commit it to the transcript or count
      // it as a real response (it bills tokens but says/does nothing). The voice layer repairs the silence.
      // Delegated tools above are real work, so a turn that ran them is never "empty".
      const emptyTurn = toolCalls.length === 0 && delegatedTools.length === 0 && contentText(res.content ?? '').trim() === '';
      if (!emptyTurn) lastProgressAt = Date.now(); // productive step (text/tool/delegated) → reset the idle timeout window
      if (!emptyTurn) {
        this.transcript.push({
          role: 'assistant',
          content: res.content ?? '',
          ...(toolCalls.length ? { tool_calls: toolCalls } : {}),
        });
      }

      if (toolCalls.length === 0) {
        // A message injected while the model was composing its final reply re-opens the turn (CC
        // parity) instead of being lost to the post-turn queue — the model answers it in-place.
        if (this.drainInjections()) continue;
        // Closure reflex: a delegated runtime ended on a tool with no wrap-up. Nudge ONE closing step.
        // Ask for the ANSWER, not a process report: the tool records are already in the transcript, so a
        // "summarize what you did" phrasing made the model narrate its own turn ("I ran 4 searches, they
        // were interrupted, want me to retry?") instead of answering the user — and invited it to re-run
        // work it had already completed. Name those failure modes so it can't slide back into them. Do NOT
        // assert the results are complete (a tool may have errored) — that would push it to fabricate;
        // instead give it an explicit out to report the failure.
        if (o.closeDelegatedTurns && res.endedOnToolNoText && !closureNudged && steps < o.maxSteps) {
          closureNudged = true;
          log.verbose('delegated turn went silent after a tool — nudging a closing answer');
          this.transcript.push({
            role: 'user',
            content: 'You ran tools but did not reply. Answer the user now using the tool results above; '
              + 'if a tool failed, say so plainly. Do NOT re-run those tools, do NOT narrate what you did, '
              + 'and do NOT ask for permission to continue.',
          });
          continue;
        }
        log.verbose(`completed in ${steps} step(s)`);
        await this.ctx.jobs?.drain(); // let background jobs finish + commit before we call the run done (no lost writes)
        this.releaseStaleImages(); // inline screenshots are one-shot context — don't immortalize MB of base64
        await this.activeHooks?.onStop?.(res.content ?? ''); // [hooks] fire once on clean stop
        return { text: res.content ?? '', steps, finishReason: 'stop', messages: this.transcript, usage, usageEstimated };
      }

      // loop detection: the model emitting the same tool-call batch over and over.
      const fp = toolCalls.map((tc) => tc.function.name + ':' + (tc.function.arguments ?? '')).join('|');
      repeats = fp === lastFp ? repeats + 1 : 1;
      lastFp = fp;
      if (o.maxRepeats && repeats >= o.maxRepeats) return kill('loop');

      toolCallsTotal += toolCalls.length;
      if (o.maxToolCalls && toolCallsTotal > o.maxToolCalls) return kill('max_tool_calls');

      for (const tc of toolCalls) {
        if (o.signal?.aborted) return kill('aborted'); // cancelled mid-batch → don't run the rest
        const raw = await this.dispatch(tc);
        let content: MessageContent;
        if (typeof raw === 'string') { content = raw; }
        else {
          const parts: ContentPart[] = [{ type: 'text', text: raw.text }];
          for (const img of raw.images ?? []) parts.push(imagePart(`data:${img.mimeType};base64,${img.data}`));
          content = parts;
        }
        this.transcript.push({ role: 'tool', tool_call_id: tc.id, name: tc.function.name, content });
      }
      // AskUserQuestion with no reachable human: stop and hand the question to the CALLER rather than
      // let the model pick for them. Runs AFTER the batch so the tool result is in the transcript —
      // the session persists cleanly and a resume continues from the answer. A clean pause, not a
      // kill: background jobs drain + commit (unlike kill(), which discards them).
      if (this.ctx.needsInput) {
        const question = this.ctx.needsInput;
        this.ctx.needsInput = undefined;
        log.warn(`needs_input: ${question.question} (steps=${steps})`);
        await this.ctx.jobs?.drain();
        const text = [
          `OPEN QUESTION (run paused — no human was reachable): ${question.question}`,
          ...question.options.map((o) => `  • ${o.label}${o.description ? ` — ${o.description}` : ''}`),
        ].join('\n');
        return { text, steps, finishReason: 'needs_input', messages: this.transcript, usage, usageEstimated, question };
      }
      // Mid-turn steering: fold queued user input in after the tool results so the next model step
      // sees it alongside them (Claude-Code parity). No-op unless the host called inject() this step.
      this.drainInjections();
    }
  }

  /**
   * Drain a streamed chat() response: emit each text delta to the host
   * (`{kind:'text_delta'}`) and fold all chunks back into the single
   * ChatResponse the non-stream path would have returned.
   */
  /**
   * Idle-stall watchdog for one streamed model request. Returns a linked AbortController `signal` to
   * hand to `chat()`, a `poke()` to reset the idle timer on each chunk, a `reclassify()` that turns a
   * watchdog-fired abort into an Error{code:'stall'} (leaving a parent/user abort untouched), and a
   * `clear()` to disarm. Aborts the request if no `poke()` (or completion) happens within `stallMs`.
   * `stallMs = 0` disables the timer but still links parent-abort → child so cancellation propagates.
   */
  private armStallWatchdog(parent: AbortSignal | undefined, stallMs: number) {
    const ac = new AbortController();
    let fired = false;
    const onParentAbort = () => ac.abort();
    if (parent?.aborted) ac.abort();
    else parent?.addEventListener('abort', onParentAbort, { once: true });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poke = () => {
      if (!stallMs) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { fired = true; ac.abort(); }, stallMs);
    };
    poke(); // arm before the request so time-to-first-chunk is guarded too
    return {
      signal: ac.signal,
      poke,
      clear: () => { if (timer) clearTimeout(timer); parent?.removeEventListener('abort', onParentAbort); },
      // Distinguish OUR abort (silent provider) from a parent/user cancel, which must stay an abort.
      reclassify: (err: unknown) => {
        if (fired && !parent?.aborted) {
          const e = new Error(`model stalled: no stream output for ${stallMs >= 1000 ? `${Math.round(stallMs / 1000)}s` : `${stallMs}ms`}`) as Error & { code: string };
          e.code = 'stall';
          return e;
        }
        return err;
      },
    };
  }

  /** A clean-stop turn that carries no assistant text AND no tool calls (native or delegated) — a
   *  non-result the model produced by going silent. Callers retry it rather than report "done". */
  private isEmptyStop(res: ChatResponse): boolean {
    const stop = res.finishReason === 'stop' || res.finishReason == null;
    return stop && (res.toolCalls?.length ?? 0) === 0 && (res.delegatedTools?.length ?? 0) === 0 && contentText(res.content ?? '').trim() === '';
  }

  private async consumeStream(stream: AsyncIterable<StreamChunk>, poke?: () => void): Promise<ChatResponse> {
    let content = '';
    let finishReason: string | undefined;
    let toolCalls: ToolCall[] | undefined;
    let usage: ChatResponse['usage'];
    // Delegated-runtime tools (cursor/claude-code loop internally) surface ONLY as display-only
    // toolActivity — accumulate + correlate by id (cursor: one `completed` event carrying everything;
    // claude-code: a `running` half with name+input, then a `completed`/`error` half with output) so
    // the loop can persist them into the transcript. First-seen order is preserved.
    const delegated = new Map<string, NonNullable<ChatResponse['delegatedTools']>[number]>();
    const delegatedOrder: string[] = [];
    // Closure signal: true while the last significant event was a delegated tool with no assistant text
    // yet after it. A trailing text chunk clears it; ending true = the runtime went silent post-tool.
    let endedOnToolNoText = false;
    for await (const chunk of stream) {
      poke?.(); // progress arrived → reset the idle-stall watchdog
      if (this.options.signal?.aborted) break; // cancelled → stop emitting now; breaking also closes the stream
      // [G3] surface extended-thinking as a distinct channel (never folded into `content` or the
      // persisted transcript — it's a transient UI stream, not history). Provider-dependent field.
      if (chunk.reasoningContent) this.options.host!.notify!({ kind: 'thinking_delta', message: chunk.reasoningContent });
      // Display-only tool activity from a self-executing backend (Cursor CLI runs its OWN tools) — surface
      // for host tool cards; never dispatched. `message` gives plain hosts a printable line.
      if (chunk.toolActivity) {
        const a = chunk.toolActivity;
        const verb = a.status === 'error' ? '✗' : a.status === 'completed' ? '✓' : '→';
        this.options.host!.notify!({ kind: 'tool_activity', id: a.id, name: a.name, input: a.input, output: a.output, status: a.status, truncated: a.truncated, message: `${verb} ${a.name}` });
        const key = a.id ?? `${a.name}:${delegatedOrder.length}`;
        const prev = delegated.get(key);
        if (!prev) { delegated.set(key, { id: key, name: a.name, input: a.input, output: a.output, status: a.status }); delegatedOrder.push(key); }
        else { // merge halves: the completed/error event carries output; the running one carries name/input
          if (a.name) prev.name = a.name;
          if (a.input !== undefined) prev.input = a.input;
          if (a.output !== undefined) prev.output = a.output;
          prev.status = a.status;
        }
        endedOnToolNoText = true; // a tool just ran; cleared below if any text follows it
      }
      if (chunk.content) {
        content += chunk.content;
        if (chunk.content.trim()) endedOnToolNoText = false; // real text after the tool → the turn has a wrap-up
        this.options.host!.notify!({ kind: 'text_delta', message: chunk.content });
      }
      if (chunk.finishReason) finishReason = chunk.finishReason;
      if (chunk.toolCalls?.length) toolCalls = chunk.toolCalls; // accumulated, latest wins
      if (chunk.usage) usage = chunk.usage; // exact usage on the terminal chunk (providers that report it; else runLoop estimates)
    }
    const delegatedTools = delegatedOrder.map((k) => delegated.get(k)!);
    return { content, ...(finishReason ? { finishReason } : {}), ...(toolCalls ? { toolCalls } : {}), ...(usage ? { usage } : {}), ...(delegatedTools.length ? { delegatedTools } : {}), ...(endedOnToolNoText ? { endedOnToolNoText: true } : {}) };
  }

  private async dispatch(tc: ToolCall): Promise<string | { text: string; images?: { mimeType: string; data: string }[] }> {
    const tool = this.activeTools.find((t) => t.name === tc.function.name);
    // [render] Parse args first but DON'T early-return on failure. An unknown-tool or bad-args call
    // (e.g. a model hallucinating a `task` tool) must still flow through the same preToolUse/notify/
    // postToolUse path as a real call — otherwise the host draws no `⚙` line and the UI looks silently
    // stuck. On parse failure keep the raw string as `args` so it still renders, and fail below.
    let args: any = {};
    let earlyError: string | undefined;
    if (!tool) earlyError = `Error: unknown tool '${tc.function.name}'`;
    try {
      args = tc.function.arguments ? JSON.parse(tc.function.arguments) : {};
    } catch (e) {
      args = tc.function.arguments;
      earlyError ??= `Error: invalid JSON arguments for ${tc.function.name}: ${String(e)}`;
    }
    const hooks = this.activeHooks; // [hooks] composed: user + plan-mode + permissions
    const call = { name: tc.function.name, args };
    const meta = { id: tc.id }; // [G4] stable per-call id so a host can correlate result↔call without serial-order assumptions
    // preToolUse is where a permission policy blocks on the user's Yes/No/Always answer — park that wait
    // so a long-standing approval prompt doesn't accrue toward the timeout kill-switch.
    const decision = await this.park(Promise.resolve(hooks?.preToolUse?.(call, meta))); // [hooks] guard before run
    if (decision?.block) {
      const blocked = `Blocked by hook: ${decision.reason ?? 'no reason given'}`;
      log.debug(`${tc.function.name} -> ${blocked}`);
      await hooks?.postToolUse?.(call, blocked, meta); // [hooks] observe the block too
      return blocked;
    }
    this.options.host?.notify?.({ kind: 'tool_use', id: tc.id ?? '', name: tc.function.name, input: args });
    // [render] surface the unknown-tool / bad-args failure through the normal result path (postToolUse
    // renders the body; notify carries isError) so it's visible instead of vanishing.
    if (earlyError) {
      log.debug(`${tc.function.name} -> ${earlyError}`);
      await hooks?.postToolUse?.(call, earlyError, meta);
      this.options.host?.notify?.({ kind: 'tool_result', id: tc.id ?? '', output: earlyError, isError: true });
      return earlyError;
    }
    let result: string;
    let images: { mimeType: string; data: string }[] | undefined;
    let threw = false;
    try {
      log.debug(`${tc.function.name}(${tc.function.arguments})`);
      // [hooks] per-call incremental-output channel: streaming tools (real Shell) push chunks via
      // ctx.emit → onToolOutput. Dispatch is serial, so binding on the shared ctx is safe; cleared
      // in finally so a straggler emit after the call settles is a silent no-op.
      this.ctx.emit = hooks?.onToolOutput ? (chunk: string) => { try { hooks.onToolOutput!(call, chunk, meta); } catch (e) { log.debug(`onToolOutput hook error: ${e}`); } } : undefined;
      const raw = await tool!.run(args, this.ctx);
      if (typeof raw === 'string') { result = raw; }
      else { result = raw.text; images = raw.images; }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      log.debug(`${tc.function.name} -> error: ${msg}`);
      result = `Error: ${msg}`;
      threw = true;
    } finally {
      this.ctx.emit = undefined; // the call settled — late emits (stray timers) go nowhere
    }
    // autoTest (opt-in): after a successful write-class tool, run a VFS test command and surface any failure
    // to the model in-band — so it never burns a turn re-running tests itself. Skipped on block/throw.
    if (!threw) result = await this.maybeAutoTest(tc.function.name, result);
    // Image-only result (e.g. a screenshot tool): give it a text marker so hosts don't render
    // "(no output)" and the model has a text anchor next to the image part.
    if (images?.length && !result) result = `[${images.length} image${images.length > 1 ? 's' : ''} attached]`;
    // Pagination ceiling: a single oversized result (a 1MB Grep, a huge Read/MCP payload) would otherwise
    // blow the context window. Crop to page 1 with a marker; a capToolResult hook can spill it losslessly.
    // Browser tools (huge DOM/screenshot payloads) get a TIGHTER per-result cap once browserTrim is set —
    // this is the dominant per-step-growth lever for a long browse (a 15k-token DOM → small stub/spill).
    // Flip browserActive here so the NEXT trimContext can also keep fewer browser bodies verbatim.
    const browser = isBrowserTool(tc.function.name);
    if (browser && this.options.browserTrim) this.browserActive = true;
    const browserBytes = browser ? this.options.browserTrim?.resultBytes : undefined;
    const baseCap = this.options.maxToolResultBytes ?? 0;
    // tighter of (user cap, browser cap); a 0 (off) on either side means "no extra limit from that side".
    const cap = browserBytes ? (baseCap > 0 ? Math.min(baseCap, browserBytes) : browserBytes) : baseCap;
    if (!threw && cap > 0 && result.length > cap) {
      const info = { tool: tc.function.name, args };
      result = this.options.capToolResult ? await this.options.capToolResult(result, info) : cropResult(result, cap);
    }
    await hooks?.postToolUse?.(call, result, meta); // [hooks] observe the result
    this.options.host?.notify?.({ kind: 'tool_result', id: tc.id ?? '', output: result, isError: threw });
    if (images?.length) {
      for (const img of images) {
        this.options.host?.notify?.({ kind: 'tool_result_image', id: tc.id ?? '', dataUrl: `data:${img.mimeType};base64,${img.data}` });
      }
    }
    return images?.length ? { text: result, images } : result;
  }

  /** `/compact` aims to land the transcript at this fraction of the context window. */
  private static readonly COMPACT_TARGET = 0.5;

  /** THE context-window cap resolution — `/compact`'s budget and the CLI's context footer/warnings both
   *  route here, so the percentage the user reads and the one compaction targets can never drift. */
  static contextCap(o: Pick<AgentOptions, 'maxContextTokens' | 'contextWindow' | 'model'> | Partial<AgentOptions>): number {
    if (o.maxContextTokens) return o.maxContextTokens;
    const w = o.contextWindow || 0;
    // Anthropic serves >200k ONLY when the request carries the `context-1m-2025-08-07` beta header, which
    // we never send (the adapter emits files-api + interleaved-thinking only). A declared 1M window is
    // therefore unusable on the wire — budgeting against it would make /compact refuse to act at 300k.
    // Lifting this is a one-liner once that beta header is emitted.
    if (w > ANTHROPIC_WINDOW_WITHOUT_1M_BETA && isAnthropicModel(o.model)) return ANTHROPIC_WINDOW_WITHOUT_1M_BETA;
    return w || 200_000;
  }

  private static readonly WRITE_CLASS = ['Write', 'Edit', 'MultiEdit', 'ApplyEdits'];

  /** Append an autoTest failure section to a write-class tool result, if configured. */
  private async maybeAutoTest(toolName: string, result: string): Promise<string> {
    const at = this.options.autoTest;
    if (!at?.command) return result;
    const set = at.tools ?? Agent.WRITE_CLASS;
    if (!set.includes(toolName)) return result;
    const r = await this.ctx.exec.execute(at.command);
    if (r.exitCode === 0) return result; // silent on pass — avoid noise
    const out = truncateOutput(((r.output ?? '') + (r.error ?? '')).replace(/\n+$/, ''));
    return `${result}\n\n[autoTest] \`${at.command}\` FAILED (exit ${r.exitCode}):\n${out}`;
  }

  /**
   * Shape the per-turn context (a VIEW — the stored transcript is never mutated). Layered, each off by default:
   *  1. coarse reduction — `compaction` (fold the dropped middle into a synthetic summary) OR
   *     `maxContextMessages` (pure drop-oldest), keeping the system message + most-recent window.
   *  2. note-taking (`keepToolOutputs`) — collapse all-but-the-recent-N tool-result bodies to one-line stubs
   *     (kills "immortal tool results": a big Read/Grep stops costing its full weight once it's old).
   *  3. token-aware backstop (`maxContextTokens`) — drop oldest messages until under an estimated token ceiling.
   * With every knob off, returns the transcript unchanged (same reference).
   */
  trimContext(): Message[] {
    const o = this.options;
    const m = this.transcript;
    let out: Message[] | null = null;
    if (o.compaction?.maxMessages && m.length > o.compaction.maxMessages) out = compact(m, o.compaction.maxMessages);
    else if (o.maxContextMessages && m.length > o.maxContextMessages) out = dropOldest(m, o.maxContextMessages);
    // Once a browser tool has actually run, keep FEWER tool-result bodies verbatim (older huge DOMs collapse
    // to one-line stubs → smaller cached prefix → smaller 0.1×cacheRead growth per step). Tighter wins; a
    // user's explicit keepToolOutputs is never LOOSENED. Off until browserActive (normal coding untouched).
    const browserKeep = this.browserActive ? o.browserTrim?.keepOutputs : undefined;
    const keep = browserKeep != null
      ? (o.keepToolOutputs ? Math.min(o.keepToolOutputs, browserKeep) : browserKeep)
      : o.keepToolOutputs;
    if (keep) out = stubOldToolResults(out ?? m, keep);
    // Decide the image outcome BEFORE the token backstop, and budget on what will ACTUALLY be sent:
    // a stale ref weighs its ~50-byte stub (not the file it will never carry), and a tool-result ref
    // weighs the whole expanded base64 (an image Read could otherwise blow the window unbudgeted).
    // Orphan-stripping runs first so the expansion window sees the real message set.
    out = expandImagesForSend(dropOrphanToolResults(out ?? m), o.keepRecentImages ?? 1);
    if (o.maxContextTokens) {
      const pre = out.length;
      out = fitTokenBudget(out, o.maxContextTokens, o.keepRecentImages ?? 1);
      // Token-cap trims used to be SILENT — surface a one-line notice (deduped per drop count)
      // so the user knows context was auto-compacted, not mysteriously forgotten.
      const dropped = pre - out.length;
      if (dropped > 0 && dropped !== this.lastTrimNotified) {
        this.lastTrimNotified = dropped;
        o.host?.notify?.({ kind: 'compaction', message: `context full — auto-trimmed ${dropped} oldest message(s) to fit ~${Math.round(o.maxContextTokens / 1000)}k tokens` });
      }
    }
    // Unconditional correctness net: a tool_result whose tool_call parent isn't in the sent set is a
    // hard API 400 (INVALID_REQUEST). Orphans arise from compaction boundaries OR a resumed transcript
    // that was folded in a prior session — strip them no matter which knobs are on. Same ref if clean.
    // (fitTokenBudget can create new head orphans; it guards its own head, this catches the rest.)
    return dropOrphanToolResults(out);
  }
}

/** system (if any) + the most-recent (max - head) messages — pure drop-oldest. */
function dropOldest(m: Message[], max: number): Message[] {
  const head = m[0]?.role === 'system' ? [m[0]] : [];
  return [...head, ...m.slice(-(max - head.length))];
}

/** Anthropic's usable context without the `context-1m-2025-08-07` beta header (see Agent.contextCap). */
const ANTHROPIC_WINDOW_WITHOUT_1M_BETA = 200_000;
const isAnthropicModel = (m?: string): boolean => !!m && (m.startsWith('anthropic/') || /(^|\/)claude-/.test(m));

/** ~4 chars/token estimate over content + serialized tool_calls. THE estimator — the CLI footer,
 *  `/context` and `/compact` all route here so what the user sees and what compaction targets can't
 *  drift apart. Images are weighed AS SENT (`sendBytes`): only the recent window's refs cost their
 *  file, stale ones cost the stub. Budgeting a ref at full file size while the wire carries a 50-byte
 *  stub made `/compact` chase a target it could never reach and drop-oldest the conversation away. */
export function estimateTokens(m: Message[], keepRecentImages = 1): number {
  return Math.ceil(sendBytes(m, keepRecentImages) / 4);
}

/**
 * Note-taking: keep the most-recent `keep` tool-result messages verbatim; collapse older, bulky ones to a
 * one-line stub that still names the tool + its file (so the model can re-pull). Structure (role/tool_call_id/
 * name) is preserved so tool_use↔tool_result pairing stays valid; only `content` is replaced, on COPIES.
 */
/** Crop an oversized tool result to page 1 (cut at a line boundary) with a marker telling the model it
 *  was cropped and how to proceed. The default (lossy) handler when no capToolResult hook spills it. */
export function cropResult(result: string, cap: number): string {
  const head = result.slice(0, cap);
  const nl = head.lastIndexOf('\n');
  const page = nl > cap * 0.5 ? head.slice(0, nl) : head; // prefer a clean line cut if not too early
  const omitted = result.length - page.length;
  return `${page}\n\n[output cropped — showing ${page.length} of ${result.length} bytes; ${omitted} omitted. ` +
    `This is page 1. Refine your query/command to narrow it, or call the tool again with a tighter scope to see more.]`;
}

function stubOldToolResults(messages: Message[], keep: number): Message[] {
  const meta = new Map<string, string | undefined>(); // tool_call_id -> path arg (for the pointer)
  for (const msg of messages)
    for (const tc of msg.tool_calls ?? []) {
      let path: string | undefined;
      try { const a = tc.function.arguments ? JSON.parse(tc.function.arguments) : {}; if (typeof a.path === 'string') path = a.path; } catch { /* args not JSON */ }
      meta.set(tc.id, path);
    }
  const toolIdx = messages.map((x, i) => (x.role === 'tool' ? i : -1)).filter((i) => i >= 0);
  const stub = new Set(toolIdx.slice(0, Math.max(0, toolIdx.length - keep)));
  if (stub.size === 0) return messages;
  return messages.map((x, i) => {
    const text = contentText(x.content);
    if (!stub.has(i) || text.length <= 120) return x; // small results aren't worth stubbing
    const where = meta.get(x.tool_call_id ?? '');
    const lines = text.split('\n').length;
    return { ...x, content: `[${x.name ?? 'tool'}${where ? ` ${where}` : ''} output elided — ${lines} lines; re-run the tool to view]` };
  });
}

/**
 * Elide oversized message BODIES until the transcript fits `target`. Progressive caps: shave the
 * biggest first, tighten only if still over. Tool results are the usual whales, but a single 1MB user
 * paste is shortened in place too — it must never fall through to the drop-oldest backstop and take the
 * user's own text with it. Only `content` changes, on copies — role/tool_call_id/name stay intact so
 * tool_use↔tool_result pairing holds; the system head is never touched.
 */
/** Drop inline base64 images — heaviest message first — until the transcript fits `target`. Unlike text,
 *  an image part can't be shortened, so an oversized screenshot must be elided (to `IMAGE_ELIDE_STUB`)
 *  BEFORE the text ladder starts paying for it by shredding, then dropping, the conversation itself.
 *  Stops as soon as we're under budget, so a small/recent image survives when text is the real weight. */
function elideOversizedImages(messages: Message[], target: number, protectNewest = false, keepRecentImages = 1): Message[] {
  let out = messages;
  // REF-AWARE: post-image-refs every image in a live transcript is a ref, so selecting on inline
  // base64 alone made both ladder passes a no-op (and `protectNewest` protect nothing) — the ladder
  // then paid for the image by shredding, and finally dropping, the user's text.
  const withImages = out.map((_, i) => i).filter((i) => messageHasImage(out[i].content));
  // `protectNewest` holds back the most recent image-bearing message — the one `releaseStaleImages`
  // guarantees survives; the caller re-runs unprotected only if the text ladder still can't fit us.
  const newest = protectNewest ? withImages[withImages.length - 1] : -1;
  // Order by WIRE weight, not stored weight: a stale ref already weighs the stub, so eliding it frees
  // nothing — shave the in-window image that actually costs the file first.
  const weight = sendBytesPer(out, keepRecentImages);
  const heaviestFirst = withImages
    .filter((i) => i !== newest)
    .sort((a, b) => weight[b] - weight[a]);
  for (const i of heaviestFirst) {
    if (estimateTokens(out, keepRecentImages) <= target) break;
    const c = elideImages(out[i].content!);
    if (c === out[i].content) continue;
    out = out.map((m, j) => (j === i ? { ...m, content: c } : m));
  }
  return out;
}

function capOversizedBodies(messages: Message[], target: number, keepRecentImages = 1): Message[] {
  let out = messages;
  for (const cap of [8000, 4000, 2000, 800, 300, 120]) {
    if (estimateTokens(out, keepRecentImages) <= target) break;
    out = out.map((x) => (x.role === 'system' ? x : elideBody(x, cap)));
  }
  return out;
}

/** Elide one message's text down to `cap` chars. Array content keeps its non-text parts (images)
 *  intact — only the text parts are shaved — and the note says so, rather than dropping them invisibly. */
function elideBody(x: Message, cap: number): Message {
  const note = (bytes: number, imgs: number) =>
    `\n\n[${bytes} bytes elided by /compact${x.role === 'tool' ? ' — re-run the tool to view' : ''}` +
    `${imgs ? `; ${imgs} image part(s) KEPT in full — they still cost context` : ''}]`;
  if (!Array.isArray(x.content)) {
    const text = contentText(x.content);
    if (text.length <= cap) return x;
    return { ...x, content: `${text.slice(0, cap)}${note(text.length - cap, 0)}` };
  }
  const imgs = x.content.filter((p) => p.type !== 'text').length;
  let left = cap;
  let elided = 0;
  // EMPTY text parts are dropped, never emitted: once `left` hits 0 every later text part would otherwise
  // become `{type:'text', text:''}` — which the Anthropic API rejects outright.
  const parts: ContentPart[] = [];
  for (const p of x.content) {
    if (p.type !== 'text') { parts.push(p); continue; }
    const t = p.text ?? '';
    if (t.length <= left) { left -= t.length; if (t) parts.push(p); continue; }
    const keep = left; left = 0; elided += t.length - keep;
    if (keep > 0) parts.push({ ...p, text: t.slice(0, keep) });
  }
  // Nothing shaved: only rewrite if we actually dropped pre-existing empty parts (never to an empty body).
  if (!elided) return parts.length === x.content.length || !parts.length ? x : { ...x, content: parts };
  // The note is always appended, so the message can never end up with zero content parts.
  return { ...x, content: [...parts, { type: 'text', text: note(elided, imgs).trimStart() }] };
}

/** All tool_call ids present on assistant messages — one O(n) pass instead of a scan per result. */
const callIdSet = (msgs: Message[]): Set<string> => {
  const ids = new Set<string>();
  for (const m of msgs) if (m.role === 'assistant') for (const tc of m.tool_calls ?? []) ids.add(tc.id);
  return ids;
};

/** Drop every orphan tool_result (one whose tool_call isn't in the set) — sending one is a hard API
 *  400. Anywhere in the sequence, not just the head. Returns the same array reference when clean. */
function dropOrphanToolResults(messages: Message[]): Message[] {
  const ids = callIdSet(messages);
  const ok = (m: Message) => m.role !== 'tool' || ids.has(m.tool_call_id ?? '');
  return messages.every(ok) ? messages : messages.filter(ok);
}

/**
 * Token-aware backstop: keep the system head, drop oldest body messages until under `maxTokens`.
 * The window may empty entirely (a single message can exceed the whole budget), and it is never
 * left starting on an ORPHAN tool_result (one whose tool_call isn't in the window) — that would
 * break tool_use↔tool_result pairing and get rejected by the API. If even [system] is over budget
 * (e.g. a huge memory index), we can't trim further — warn; the hard `maxTokens` kill-switch is the real guard.
 */
function fitTokenBudget(messages: Message[], maxTokens: number, keepRecentImages = 1): Message[] {
  // Incremental accounting: estimate each message once, subtract as we drop — O(n), not O(n²)
  // (re-estimating the whole window per dropped message walks every char repeatedly). Per-message
  // weights come from the SHARED window (`sendBytesPer`), so a stale ref isn't charged its file.
  const per = sendBytesPer(messages, keepRecentImages).map((b) => Math.ceil(b / 4));
  let total = per.reduce((a, b) => a + b, 0);
  if (total <= maxTokens) return messages;
  const head = messages[0]?.role === 'system' ? [messages[0]] : [];
  let from = head.length; // body = messages[from..]
  while (from < messages.length && total > maxTokens) total -= per[from++];
  const ids = callIdSet(messages.slice(from));
  while (from < messages.length && messages[from].role === 'tool' && !ids.has(messages[from].tool_call_id ?? '')) total -= per[from++];
  if (total > maxTokens)
    log.warn(`context ~${total} tok still over maxContextTokens=${maxTokens} after trimming (system head can't be dropped)`);
  return [...head, ...messages.slice(from)];
}

/** system (if any) + synthetic summary of the dropped middle + most-recent window; fits within `max`. */
function compact(m: Message[], max: number, focus?: string): Message[] {
  const hasSystem = m[0]?.role === 'system';
  const head = hasSystem ? [m[0]] : [];
  // Reserve one slot for the summary, the rest for the most-recent window. Bound the tail
  // to the post-head slice so a short transcript (max >= length) can't re-include the head.
  const tailCount = Math.max(1, max - head.length - 1);
  const tail = m.slice(head.length).slice(-tailCount);
  const dropped = m.slice(head.length, m.length - tail.length);
  if (dropped.length === 0) return [...head, ...tail];
  return [...head, { role: 'system', content: summarize(dropped, focus) }, ...tail];
}

/** Header of a synthetic recap produced by `summarize` — how a LATER pass recognizes its own output. */
const SUMMARY_HEAD = '[summary of ';
/** Upper bound on the message count a single prior recap may claim (untrusted input — see summarize). */
const MAX_CARRIED_COUNT = 10_000;
const isSyntheticSummary = (m: Message): boolean =>
  m.role === 'system' && typeof m.content === 'string' && m.content.startsWith(SUMMARY_HEAD);

/** Lightweight, deterministic, LLM-free recap of a span of messages.
 *  A recap from an EARLIER pass inside the span is carried forward (its message count and its detail
 *  lines), never silently thrown away — the escalation ladder re-folds an already-folded transcript, and
 *  a recap that discards its predecessor would lie about how much history it stands for. */
function summarize(messages: Message[], focus?: string): string {
  const tools = new Set<string>();
  const files = new Set<string>();
  const carried: string[] = []; // detail lines from prior recaps in this span (deduped, bounded)
  let carriedCount = 0; // messages those recaps already stood for
  let priors = 0;
  let lastAssistant = '';
  for (const msg of messages) {
    if (isSyntheticSummary(msg)) {
      priors++;
      const [head, ...rest] = String(msg.content).split('\n');
      // Clamp: a recap head is data, not a promise — a restored session or a hook-written system message
      // could carry `[summary of 999999 …]` and we must not absorb it verbatim.
      carriedCount += Math.min(Math.max(Number(/of (\d+)/.exec(head)?.[1]) || 0, 0), MAX_CARRIED_COUNT);
      for (const l of rest) if (l.trim() && !carried.includes(l) && carried.length < 20) carried.push(l);
      continue;
    }
    for (const tc of msg.tool_calls ?? []) {
      tools.add(tc.function.name);
      try {
        const args = tc.function.arguments ? JSON.parse(tc.function.arguments) : {};
        if (typeof args.path === 'string') files.add(args.path);
      } catch {
        /* arguments not JSON — skip file extraction */
      }
    }
    if (msg.role === 'assistant' && msg.content) lastAssistant = contentText(msg.content);
  }
  const lines = [`${SUMMARY_HEAD}${messages.length - priors + carriedCount} earlier message(s)]`];
  if (tools.size) lines.push(`tools used: ${[...tools].join(', ')}`);
  if (files.size) lines.push(`files touched: ${[...files].join(', ')}`);
  if (lastAssistant) lines.push(`last assistant: ${lastAssistant}`);
  if (carried.length) lines.push('earlier recap:', ...carried.map((l) => `  ${l}`));
  // Focused compaction: keep lines from the dropped span that mention any focus keyword,
  // verbatim (bounded), so "/compact keep the API details" doesn't lose them to the recap.
  if (focus?.trim()) {
    const words = focus.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
    const kept: string[] = [];
    // THIS span's own messages are scanned FIRST: a prior recap sits at index 0 and would otherwise eat the
    // whole budget, so repeated `/compact <focus>` would keep only the OLDEST hits and drop the newest.
    // (Older hits still reach the recap via `carried` above.)
    const ordered = [...messages.filter((m) => !isSyntheticSummary(m)), ...messages.filter(isSyntheticSummary)];
    outer: for (const msg of ordered) {
      if (msg.role === 'tool') continue; // tool bodies are bulk noise; user/assistant text carries the decisions
      for (const line of contentText(msg.content).split('\n')) {
        const l = line.toLowerCase();
        if (line.trim() && words.some((w) => l.includes(w))) {
          kept.push(line.trim().slice(0, 200));
          if (kept.length >= 12) break outer;
        }
      }
    }
    if (kept.length) lines.push(`preserved (re: ${focus.trim()}):`, ...kept.map((l) => `  ${l}`));
  }
  return lines.join('\n');
}

function lastAssistantText(messages: Message[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') return contentText(messages[i].content);
  }
  return '';
}
